import React, {useRef, useState} from "react";
import {FiMaximize2, FiMinimize2} from "react-icons/fi";
import {HiPlus, HiX} from "react-icons/hi";
import {MdHistory} from "react-icons/md";
import {useDispatch, useSelector} from "react-redux";
import {
  setShowBigChatbox,
  setShowHistoryDropdown,
  setShowNormalChatbox,
  setShowSearchInterface,
} from "../../redux/slices/chat";
import {useChatService} from "../../services";
import ChatHistoryDropdown from "./ChatHistoryDropdown";
import Portal from "../Portal";

interface ChatbotHeaderProps {
  onMinimize: () => void;
  onCreateNewChatClick: () => void;
  onChatClick: (chat_id: number) => void;
  onCloseChatModal: () => void;
}

const ChatbotHeader: React.FC<ChatbotHeaderProps> = ({ onCreateNewChatClick, onChatClick, onMinimize, onCloseChatModal }) => {
  const dispatch = useDispatch();
  const { getChatHistoryList } = useChatService();
  const historyToggleRef = useRef<HTMLButtonElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const {
    showBigChatbox,
    showHistoryDropdown,
    showNormalChatbox,
    showSearchInterface,
    chatHistoryByDate
  } = useSelector((state?: any) => state.chatMessages);

  const openNormalChatbox = () => {
    dispatch(setShowNormalChatbox(true));
  }

  const closeSearchInterface = () => {
    dispatch(setShowSearchInterface(false));
  }

  const toggleHistoryDropdown = () => {
    dispatch(setShowHistoryDropdown(!showHistoryDropdown));
  }

  const closeHistoryDropdown = () => {
    dispatch(setShowHistoryDropdown(false));
  }

  const handleMaximize = () => {
    dispatch(setShowBigChatbox(!showBigChatbox));
    openNormalChatbox();

    if (showBigChatbox && showSearchInterface) {
      closeSearchInterface();
    }
  };

  const handleToggleHistoryDropdown = () => {
    if (!showBigChatbox) {
      if (!showHistoryDropdown) {
        const rect = historyToggleRef.current?.getBoundingClientRect();
        if (rect) {
          // 288px width (w-72). Align right edge with icon, add small gap below.
          const dropdownWidth = 288;
          const left = Math.max(8, rect.right - dropdownWidth); // keep within viewport
          const top = rect.bottom + 6;
          setDropdownPosition({ top, left });
        }
      }

      toggleHistoryDropdown();

      if (showHistoryDropdown) {
        const hasExistingChats = (chatHistoryByDate.today?.length || 0) +
          (chatHistoryByDate.yesterday?.length || 0) +
          (chatHistoryByDate.older?.length || 0) > 0;

        if (!hasExistingChats) {
          getChatHistoryList(null, { page: 1, per_page: 20 });
        }
      }
    }
  };

  return (
    <div
      className=" sticky top-0 flex items-center justify-between p-2 sm:p-3 md:p-4 bg-white dark:bg-tertiary-dark text-gray-800 dark:text-white border-b border-secondary-light dark:border-secondary-dark"
    >
      <div className="flex items-center">
        <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white dark:bg-gray-800 mr-1 flex items-center justify-center">
          <img src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp" className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" alt="Cadence Logo" />
        </div>
        <span className="text-base sm:text-lg font-medium text-gray-800 dark:text-white">
          CadenceAI
        </span>
      </div>

      <div className="flex items-center space-x-1">
        {/* New Chat pill animation – compact view only */}
        {!showBigChatbox && (
          <button
            onClick={onCreateNewChatClick}
            className="group flex flex-row-reverse items-center cursor-pointer overflow-hidden bg-blue-600 text-white rounded-full transition-all duration-300 p-1"
          >
            <HiPlus className="w-4 h-4  transition-transform duration-300 group-hover:scale-110" />
            <span className="max-w-0 overflow-hidden group-hover:max-w-[6rem] transition-all duration-500 ease-out whitespace-nowrap text-xs group-hover:pl-1 group-hover:pr-1 ">
              New Chat
            </span>
          </button>
        )}

        {!showBigChatbox && (
          <button
            ref={historyToggleRef}
            onClick={handleToggleHistoryDropdown}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full cursor-pointer text-gray-600 dark:text-gray-300"
            title="Chat History"
          >
            <MdHistory className="w-4 h-4  sm:w-5 sm:h-5" />
          </button>
        )}

        <button
          onClick={onMinimize}
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full cursor-pointer text-gray-600 dark:text-gray-300"
          title="Minimize"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>

        {showNormalChatbox &&
          <button
            onClick={handleMaximize}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full cursor-pointer text-gray-600 dark:text-gray-300"
          >
            {showBigChatbox ?
              <FiMinimize2 title="Switch to small chatbox" className="w-3 h-3 sm:w-4 sm:h-4" />
            :
              <FiMaximize2 title="Maximize" className="w-3 h-3 sm:w-4 sm:h-4" />
            }
          </button>
        }

        <button
          onClick={onCloseChatModal}
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full cursor-pointer text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
        >
          <HiX className="w-3 h-3 sm:w-4 sm:h-4" />
        </button>
      </div>

      <Portal>
        <ChatHistoryDropdown
          isVisible={showHistoryDropdown && !showBigChatbox}
          position={dropdownPosition}
          onClose={closeHistoryDropdown}
          onChatClick={onChatClick}
        />
      </Portal>
    </div>
  )
};

export default ChatbotHeader;